/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import BrandGroupList from '@apps/components/BrandGroupList';
import CarNewsListing from '@apps/components/CarNewsListing';
import getAds from '@apps/config/API/ads.config';
import CarFaq from '@apps/containers/CarFaq';
import LoanCalculatorContainer from '@apps/containers/LoanCalculatorContainer';
import Ratings from '@apps/containers/Ratings';
import RecommendCarList from '@apps/containers/RecommendCarList';
import RelatedCarSection from '@apps/containers/RelatedCarSection';
import SameRangeCarSection from '@apps/containers/SameRangeCarSection';
import ServiceMaintenance from '@apps/containers/ServiceMaintenance';
import SpecificationContainer from '@apps/containers/SpecificationContainer';
import {
  CarDetailSectionWrapper,
  LeftSectionWrapper,
  RightSectionWrapper,
} from '@apps/screens/CarDetail/styles';
import { Theme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import { BorderTitle, TitleDivider } from '@stack/ui/components';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';

import CarDimensionBox from '../CarDimension';
import CarHighlightBox from '../CarHighlight';
import CarKeyDetailBox from '../CarKeyDetail';
// import CarKeyDetailBox from '../CarKeyDetail';
import CarOverview from '../CarOverview';
import CarPhotos from '../CarPhotos';
import CarProfile from '../CarProfile';
import CarVideos from '../CarVideos';
import SpecAndFeature from '../SpecAndFeature';

import { createSectionData } from './helper';
import CarDetailSectionContentProps, {
  CarDetailSectionContentRefProps,
  CarDetailSectionType,
} from './props';
import SectionItem from './SectionItem';
import {
  Error404Button,
  Error404Caption,
  Error404CaptionWrapper,
  Error404Container,
  Error404Title,
} from './styles';

/**
 * ===========================
 * MAIN
 * ===========================
 */
const CarDetailSectionContentView: React.ForwardRefRenderFunction<
  CarDetailSectionContentRefProps,
  CarDetailSectionContentProps
> = (props, ref) => {
  const {
    data,
    containerRef,
    navHeaderRef,
    navItemRef,
    selectedVariant,
    settings,
    stickyCarDetailRef,
    webView,
    setSelectedVariant,
    onSectionsCallback,
  } = props;

  // ======================= HOOKS
  const isTabletView = useMediaQuery((theme: Theme) =>
    theme?.breakpoints?.between('sm', 1000)
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [googleAds, setGoogleAds] = useState<any>(null);
  const { spec, serviceData } = createSectionData(data);
  const { lg: isMobile, isWeb } = useGetResponsive();
  const { t } = useTranslation('common');
  useEffect(() => {
    setSelectedVariant(spec?.[0]);
  }, []);

  const onFetchGoogleAds = async (slug: 'sm_car_detail' | 'xl_car_detail') => {
    try {
      const res = await getAds(slug);
      setGoogleAds(res);
    } catch (err) {
      console.log('google ads err', err);
    }
  };

  // ======================= EFFECTS
  useEffect(() => {
    onFetchGoogleAds(isMobile ? 'sm_car_detail' : 'xl_car_detail');
  }, [isMobile]);

  // ======================= VARIABLES
  const relatedNews = data?.data?.related_news
    ?.map((item: { post: any }) => item?.post)
    ?.map((post: any) => ({
      category_name: post?.post_category_name?.en,
      post_id: post?.post_id,
      post_title: post?.post_title?.en,
      post_author: post?.post_author?.user_fullname,
      post_author_username: post?.post_author?.username,
      post_author_profile_image: post?.post_author?.photo_url,
      post_slug: post?.post_slug,
      post_category: post?.post_category,
      post_description: post?.post_description?.en,
      post_short_description: post?.post_short_description?.en,
      post_tag: [],
      post_created: post?.post_created
        ? dayjs(new Date(post?.post_created)).format('DD-MM-YYYY hh:mm A')
        : null,
      post_image: post?.post_full_url?.en,
      post_image_full: post?.post_full_url?.en,
      post_thumbnail_id: post?.post_full_url?.en,
      post_language: post?.post_language,
      post_available_language: null,
      images_arr: [],
    }));
  const lowestPrice = data?.data?.lowest_price;
  const highestPrice = data?.data?.highest_price;
  const isTBC =
    parseFloat(lowestPrice || '0') === 0 &&
    parseFloat(highestPrice || '0') === 0;

  // ======================= SECTION DATA
  const renderMobileViewCarInfo = () => {
    if (!isMobile) return null;
    return (
      <Box
        sx={{
          gap: '20px',
          display: 'flex',
          flexDirection: 'column',
          marginTop: '0px',
        }}
      >
        {/* <CarKeyDetailBox data={selectedVariant} /> */}

        {data?.data?.car_dimension_image_full && (
          <CarDimensionBox
            car_dimension_image_full={data?.data?.car_dimension_image_full}
            carTitle={carTitle}
          />
        )}

        <SpecAndFeature
          webView={webView}
          selected={selectedVariant}
          onSelect={setSelectedVariant}
          data={spec}
          showSpec={false}
          showTitle={false}
        />

        <Box
          sx={{
            width: '100%',
            aspectRatio: 320 / 100,
            backgroundColor: '#F1f1f1',
            display: webView || isEmpty(googleAds) ? 'none' : 'block',
          }}
        >
          <Image
            src={
              isMobile
                ? googleAds?.ads_block_placement_media_mobile_url
                : googleAds?.ads_block_placement_media_desktop_url
            }
            width={'0'}
            height={'0'}
            sizes="100vw"
            style={{
              height: '100px',
              width: '100%',
              objectFit: 'contain',
            }}
            alt={googleAds?.ads_block_placement_title}
          />
        </Box>

        <SectionItem sectionKey="Ratings" hide={hideMobileReviews}>
          <Ratings
            data={{
              rating: data?.data?.new_car_user_experience?.map(
                (e: { result_rating: number }) => (e.result_rating / 100) * 5
              ),
              totalUserRated: data?.data?.new_car_total_user_rating ?? 0,
              disappointment: data?.data?.new_car_tag?.disappointment?.map(
                (e: { new_car_tag_name: string }) => e?.new_car_tag_name
              ),
              satisfaction: data?.data?.new_car_tag?.satisfaction?.map(
                (e: { new_car_tag_name: string }) => e?.new_car_tag_name
              ),
            }}
          />
        </SectionItem>
      </Box>
    );
  };
  const installmentRate = Number(settings.installment_rate);
  const depositRate = Number(settings.car_deposit_rate);
  const loanTenure = Number(settings.car_loan_tenure);
  const detail = data?.data;
  const carBrand = detail?.car_brand_name;
  const carTitle = detail?.new_car_title;
  const hideMobileReviews = isEmpty(data?.data?.reviews) || isWeb;
  const hideRatings = isMobile && isEmpty(detail?.new_car_user_experience);

  const sections: CarDetailSectionType[] = [
    {
      key: 'Reviews',
      title: 'Reviews',
      isEmpty: isEmpty(detail?.reviews) || !isWeb,
      component: <CarOverview data={detail?.reviews} />,
    },
    {
      key: 'Photos',
      title: 'Photos',
      isEmpty: isEmpty(detail?.new_car_gallery),
      component: (
        <CarPhotos
          newCarGallery={detail?.new_car_gallery}
          carBrand={carBrand}
          carTitle={carTitle}
        />
      ),
    },
    {
      key: 'SpecsAndEquipments',
      title: 'Specs & Equipments',
      isEmpty: isEmpty(detail?.car_spec_and_equipment),
      component: (
        <SpecificationContainer data={detail?.car_spec_and_equipment} />
      ),
    },
    {
      key: 'ServiceMaintenance',
      title: 'Service Maintenance',
      isEmpty: isEmpty(serviceData),
      component: <ServiceMaintenance serviceData={serviceData} />,
    },
    {
      key: 'FAQs',
      title: 'FAQs',
      isEmpty: isEmpty(detail?.new_car_faq),
      component: (
        <CarFaq
          data={detail?.new_car_faq?.map(
            (e: {
              new_car_faq_description: string;
              new_car_faq_title: string;
            }) => ({
              title: e?.new_car_faq_title,
              answer: e?.new_car_faq_description,
            })
          )}
        />
      ),
    },
  ];

  // ======================= EVENTS
  const scrollToSection = useCallback(
    (key: string, extra?: number) => {
      const container = containerRef.current;
      const navHeader = navHeaderRef.current;
      if (container && navHeader) {
        const targetElement = container.querySelector(
          `#carDetailSection_${key}`
        );

        if (targetElement) {
          const navHeaderHeight =
            (navHeader?.getBoundingClientRect().height ?? 0) +
            (webView ? 18 : 60);
          const containerRect = container.getBoundingClientRect();
          const targetRect = targetElement.getBoundingClientRect();
          const offsetTop =
            targetRect.top - containerRect.top + container.scrollTop;
          const isFirstItem = key === 'Highlight';

          container.scrollTo({
            top: isFirstItem
              ? 0 + (extra || 0)
              : offsetTop - navHeaderHeight + (extra || 0),
            behavior: 'smooth',
          });
        }
      }
    },
    [containerRef, navHeaderRef]
  );

  const sectionContent = sections.filter((item) => !item?.isEmpty);
  const webNavItems = [
    {
      key: 'Highlight',
      title: 'Highlights',
    },
    {
      key: 'SpecsAndFeatures',
      title: 'Modal Variant',
      isEmpty: isEmpty(data?.data?.car_spec_and_equipment),
    },
    {
      key: 'LoanCalculator',
      title: 'Loan Calculator',
      isEmpty: data?.data?.lowest_price === 0 || isTBC,
    },
    {
      key: 'Videos',
      title: 'Videos',
      isEmpty: isEmpty(detail?.new_car_video),
    },
    {
      key: 'Reviews',
      title: 'Reviews',
      isEmpty: isEmpty(data?.data?.reviews),
    },
    {
      key: 'Photos',
      title: 'Photos',
      isEmpty: isEmpty(data?.data?.new_car_gallery),
    },
    {
      key: 'SpecsAndEquipments',
      title: 'Specs & Equipments',
      isEmpty: isEmpty(data?.data?.car_spec_and_equipment),
    },
    {
      key: 'ServiceMaintenance',
      title: 'Service Maintenance',
      isEmpty: isEmpty(serviceData),
    },
    {
      key: 'FAQs',
      title: 'FAQs',
      isEmpty: isEmpty(data?.data?.new_car_faq),
    },
    ...(!webView
      ? [
          {
            key: 'RelatedNews',
            title: 'Related News',
          },
        ]
      : []),
    {
      key: 'PopularBrands',
      title: 'Popular Brands',
    },
    {
      key: 'SameBrand',
      title: 'Same Brands',
      isEmpty: isEmpty(detail?.same_brand),
    },
    {
      key: 'SameRange',
      title: 'Same Range',
      isEmpty: isEmpty(detail?.same_range),
    },
    ...(!webView
      ? [
          {
            key: 'RecommendCars',
            title: 'Recommended Cars',
          },
        ]
      : []),
  ];

  const mobileNavItems = [
    {
      key: 'Highlight',
      title: 'Highlights',
    },
    {
      key: 'SpecsAndFeatures',
      title: 'Modal Variant',
      isEmpty: isEmpty(data?.data?.car_spec_and_equipment),
    },
    ...(!hideRatings
      ? [
          {
            key: 'Ratings',
            title: 'Ratings',
          },
        ]
      : []),
    {
      key: 'LoanCalculator',
      title: 'Loan Calculator',
      isEmpty: data?.data?.lowest_price === 0 || isTBC,
    },
    {
      key: 'Reviews',
      title: 'Reviews',
      isEmpty: isEmpty(data?.data?.reviews),
    },
    {
      key: 'Videos',
      title: 'Videos',
      isEmpty: isEmpty(detail?.new_car_video),
    },
    {
      key: 'Photos',
      title: 'Photos',
      isEmpty: isEmpty(data?.data?.new_car_gallery),
    },
    {
      key: 'SpecsAndEquipments',
      title: 'Specs & Equipments',
      isEmpty: isEmpty(data?.data?.car_spec_and_equipment),
    },
    {
      key: 'ServiceMaintenance',
      title: 'Service Maintenance',
      isEmpty: isEmpty(serviceData),
    },
    {
      key: 'FAQs',
      title: 'FAQs',
      isEmpty: isEmpty(data?.data?.new_car_faq),
    },
    ...(!webView
      ? [
          {
            key: 'RelatedNews',
            title: 'Related News',
          },
        ]
      : []),
    {
      key: 'PopularBrands',
      title: 'Popular Brands',
    },
    {
      key: 'SameBrand',
      title: 'Same Brand',
      isEmpty: isEmpty(detail?.same_brand),
    },
    {
      key: 'SameRange',
      title: 'Same Range',
      isEmpty: isEmpty(detail?.same_range),
    },
    ...(!webView
      ? [
          {
            key: 'RecommendCars',
            title: 'Recommended Cars',
          },
        ]
      : []),
  ];
  const navItems = !isWeb ? mobileNavItems : webNavItems;
  const activeNavItems = navItems
    ?.filter((item) => !item.isEmpty)
    ?.map((item) => ({
      key: item.key,
      title: item.title,
    }));

  // ======================= EFFECTS
  useImperativeHandle(
    ref,
    () => ({
      scrollToSection: (key) => {
        let scrollValue = -40;
        switch (key) {
          case 'SpecsAndFeatures':
            scrollValue = webView ? -110 : -80;
            break;
          case 'Ratings':
            scrollValue = webView ? -60 : -15;
            break;
          default:
            scrollValue = webView ? -40 : 0;
            break;
        }
        scrollToSection(key, scrollValue);
      },
      activeNavItems,
    }),
    [scrollToSection, activeNavItems]
  );

  useEffect(() => {
    onSectionsCallback?.(activeNavItems);
  }, [isWeb]);

  useEffect(() => {
    const handleScroll = () => {
      const container = containerRef.current;
      const navHeader = navHeaderRef.current;
      const navElement = navItemRef.current?.element;

      if (container && navHeader && navElement) {
        const navHeaderHeight =
          (navHeader?.getBoundingClientRect().height ?? 0) + 180;

        const elements = container.querySelectorAll(
          '[id^="carDetailSection_"]'
        );
        const navItemsElement = navElement?.querySelectorAll(
          '[id^="carDetailNavItem_"]'
        );
        let newCurrentItem = 'Highlight';
        const contentHeight =
          document.getElementById('carDetailContent')?.offsetHeight ?? 0;
        const lastElement = elements?.length
          ? elements[elements?.length - 1]
          : null;

        const carDetailHeaderOffset =
          document.getElementById('carDetailHeader')?.offsetTop ??
          0 - navHeaderHeight;

        if (carDetailHeaderOffset > (webView ? 50 : 100)) {
          stickyCarDetailRef?.current?.setShow(true);
        } else {
          stickyCarDetailRef?.current?.setShow(false);
        }

        const lastElementRect = lastElement?.getBoundingClientRect();
        const lastElementHeight = lastElementRect?.height ?? 0;
        const ESTIMATE_FOOTER_HEIGHT = -100;
        const lastElementTop =
          window.innerHeight +
          navHeader.offsetTop -
          lastElementHeight -
          ESTIMATE_FOOTER_HEIGHT;
        const isLastElement = lastElementTop >= contentHeight;

        elements.forEach((element) => {
          const rect = element.getBoundingClientRect();
          if (isLastElement && webView) {
            newCurrentItem = (lastElement?.id || '').replace(
              'carDetailSection_',
              ''
            );
            return;
          }
          if (rect.top <= navHeaderHeight) {
            newCurrentItem = element.id.replace('carDetailSection_', '');
          }
        });

        // IGNORE unused key
        const ignoreKeys = ['GoogleAds'];
        if (ignoreKeys.includes(newCurrentItem)) return;

        // Found and update new key
        if (newCurrentItem !== navItemRef.current?.focusedNavItem) {
          // Update the current focused nav item
          navItemRef.current?.setFocusedNavItem(newCurrentItem as string);

          // ============== TO AUTO CENTER IN MOBILE VIEW
          // Find the index of the item with key
          const focusedNavItemIndex = activeNavItems?.findIndex(
            (item) => item.key === newCurrentItem
          );

          // Create a new array with items up to the current focused key item
          const filteredNavItems = activeNavItems
            .slice(0, focusedNavItemIndex + 1)
            ?.map((item) => item.key);

          let totalWidth = 0;

          // Calculate the total width of nav item up to the current focused key
          if (navItemsElement) {
            navItemsElement.forEach((element) => {
              const elementId = element.id.replace('carDetailNavItem_', '');
              const activeNavItem = filteredNavItems.includes(
                elementId as string
              );
              if (activeNavItem) {
                totalWidth += element.getBoundingClientRect().width;
              }
            });
          }

          // Scroll and center the focused nav item
          const navHeaderWidth = navHeader.getBoundingClientRect().width;
          const navHeaderCenter = navHeaderWidth / 2;
          const scrollLeft = totalWidth - navHeaderCenter - 20;
          navElement?.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      }
    };

    const currentSectionRef = containerRef.current;
    currentSectionRef?.addEventListener('scroll', handleScroll);
    return () => {
      currentSectionRef?.removeEventListener('scroll', handleScroll);
    };
  }, [
    containerRef,
    navHeaderRef,
    navItemRef,
    navItemRef.current?.focusedNavItem,
    isMobile,
    activeNavItems,
  ]);

  // ======================= VIEW
  const renderError404 = () => {
    return (
      <Error404Container
        style={{
          textDecoration: 'none',
        }}
      >
        <div
          style={{
            top: '20%',
            width: '100%',
            position: 'absolute',
          }}
        >
          <Error404Title>404</Error404Title>
          <Error404CaptionWrapper>
            <Error404Caption>{t('404caption')}</Error404Caption>
          </Error404CaptionWrapper>
          {!true && (
            <Link href={'/'}>
              <Error404Button>{t('backHome')}</Error404Button>
            </Link>
          )}
        </div>
      </Error404Container>
    );
  };

  if (!data) return renderError404();
  return (
    <Stack id={'carDetailContent'} gap={{ xs: 10, lg: 2 }}>
      {/* Profile Section */}
      <SectionItem sectionKey="Highlight">
        <CarDetailSectionWrapper>
          <LeftSectionWrapper>
            <CarProfile
              carModel={detail?.new_car_title}
              carBrandImage={detail?.car_brand_image ?? ''}
              lowestPrice={detail?.lowest_price}
              highestPrice={detail.highest_price}
              estPriceWithTax={detail?.new_car_est_price_with_tax ?? null}
              carImage={detail?.car_model_image_full ?? ''}
              carBrochure={detail?.new_car_brochure}
              carPricelist={detail?.new_car_pricelist}
              carBrand={carBrand}
              carTitle={carTitle}
            />
          </LeftSectionWrapper>
          <RightSectionWrapper>
            <CarHighlightBox
              data={{
                carId: detail?.new_car_id,
                carImage: detail?.car_model_image_full,
                carName: carTitle,
                lowestPrice: detail?.lowest_price,
                highestPrice: detail?.highest_price,
                carBrandImage: detail?.car_brand_image,
                isEv: selectedVariant?.car_fuel_type_slug === 'ev',
              }}
              location_city={settings.location_city}
              location_state={settings.location_state}
              new_car_key_detail={detail?.new_car_key_detail}
              webView={webView}
            />
            <Ratings
              data={{
                rating: detail?.new_car_user_experience?.map(
                  (e: { result_rating: number }) => (e.result_rating / 100) * 5
                ),
                totalUserRated: detail?.new_car_total_user_rating ?? 0,
              }}
              showTitle={false}
              moreDetail={isMobile && !isEmpty(detail?.new_car_user_experience)}
              onClickMoreDetail={() => {
                navItemRef.current?.setFocusedNavItem('Ratings');
                scrollToSection('Ratings', webView ? -60 : -15);
              }}
            />
          </RightSectionWrapper>
        </CarDetailSectionWrapper>
      </SectionItem>

      <TitleDivider title={'Model Variant'}>
        <CarDetailSectionWrapper>
          <LeftSectionWrapper
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              ...(!isMobile && {
                paddingRight: '20px',
                borderRight: '1px solid #edeced',
              }),
            }}
          >
            {/* Model Variant */}
            <SectionItem sectionKey={'SpecsAndFeatures'}>
              <SpecAndFeature
                webView={webView}
                selected={selectedVariant}
                onSelect={setSelectedVariant}
                data={spec}
                showGraph={!isMobile}
                showTitle={false}
              />
            </SectionItem>

            {renderMobileViewCarInfo()}

            {/* Calculator */}
            <SectionItem
              sectionKey={'LoanCalculator'}
              hide={detail?.lowest_price === 0 || isTBC}
            >
              <>
                <BorderTitle
                  title="Loan Calculator"
                  containerProps={{
                    py: 5,
                  }}
                />
                <LoanCalculatorContainer
                  data={selectedVariant}
                  carImage={detail?.car_model_image_full}
                  rate={installmentRate}
                  depositRate={depositRate}
                  loanTenure={loanTenure}
                  carTitle={carTitle}
                  carBrand={carBrand}
                />
              </>
            </SectionItem>

            {/* Reviews */}
            <SectionItem sectionKey={'Reviews'} hide={hideMobileReviews}>
              <CarOverview data={detail?.reviews} />
            </SectionItem>

            {/* GoogleAds */}
            <SectionItem
              sectionKey="GoogleAds"
              hide={webView || isEmpty(googleAds) || !isMobile}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: { xs: 6 / 4, lg: 3.4 / 1 },
                  backgroundColor: '#F1f1f1',
                  pt: 3,
                }}
              >
                <Image
                  src={
                    isMobile
                      ? googleAds?.ads_block_placement_media_mobile_url
                      : googleAds?.ads_block_placement_media_desktop_url
                  }
                  width={'0'}
                  height={'0'}
                  sizes="100vw"
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: isMobile ? 'cover' : 'contain',
                  }}
                  alt={googleAds?.ads_block_placement_title}
                />
              </Box>
            </SectionItem>

            {/* Videos */}
            <SectionItem
              sectionKey={'Videos'}
              hide={isEmpty(detail?.new_car_video)}
            >
              <>
                <BorderTitle
                  title="Videos"
                  containerProps={{
                    py: 5,
                  }}
                />
                <CarVideos
                  newCarVideos={detail?.new_car_video}
                  showTitle={false}
                />
              </>
            </SectionItem>

            {/* GoogleAds */}
            <SectionItem
              sectionKey={'GoogleAds'}
              hide={webView || isEmpty(googleAds) || isMobile}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: { xs: 6 / 4, lg: 3.4 / 1 },
                  backgroundColor: '#F1f1f1',
                }}
              >
                <Image
                  src={
                    isMobile
                      ? googleAds?.ads_block_placement_media_mobile_url
                      : googleAds?.ads_block_placement_media_desktop_url
                  }
                  width={'0'}
                  height={'0'}
                  sizes="100vw"
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: isMobile ? 'cover' : 'contain',
                  }}
                  alt={googleAds?.ads_block_placement_title}
                />
              </Box>
            </SectionItem>
          </LeftSectionWrapper>
          <RightSectionWrapper>
            <Stack gap="30px">
              {/* Key Detail */}
              {!isMobile && <CarKeyDetailBox data={selectedVariant} />}

              {/* Dimensions */}
              {!isMobile && (
                <CarDimensionBox
                  car_dimension_image_full={detail?.car_dimension_image_full}
                  carTitle={carTitle}
                />
              )}

              {/* Ratings */}
              {!isMobile && !isEmpty(data?.data?.new_car_user_experience) && (
                <Ratings
                  data={{
                    rating: data?.data?.new_car_user_experience?.map(
                      (e: { result_rating: number }) =>
                        (e.result_rating / 100) * 5
                    ),
                    totalUserRated: data?.data?.new_car_total_user_rating ?? 0,
                    disappointment:
                      data?.data?.new_car_tag?.disappointment?.map(
                        (e: { new_car_tag_name: string }) => e?.new_car_tag_name
                      ),
                    satisfaction: data?.data?.new_car_tag?.satisfaction?.map(
                      (e: { new_car_tag_name: string }) => e?.new_car_tag_name
                    ),
                  }}
                />
              )}

              {/* Sidebar Google Ads */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: 324 / 250,
                  backgroundColor: '#F1f1f1',
                  display: webView || isEmpty(googleAds) ? 'none' : 'block',
                }}
              >
                <Image
                  src={
                    isMobile
                      ? googleAds?.ads_block_placement_media_mobile_url
                      : googleAds?.ads_block_placement_media_desktop_url
                  }
                  width={'0'}
                  height={'0'}
                  sizes="100vw"
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: isMobile ? 'cover' : 'contain',
                  }}
                  alt={googleAds?.ads_block_placement_title}
                />
              </Box>
            </Stack>
          </RightSectionWrapper>
        </CarDetailSectionWrapper>
      </TitleDivider>
      {sectionContent?.map((section) => {
        const { key, component, props } = section;
        return (
          <Box key={`${key}`} id={`carDetailSection_${key}`} {...props}>
            {component}
          </Box>
        );
      })}

      {!webView && (
        <SectionItem sectionKey="RelatedNews" pt={{ xs: 3, lg: 4 }}>
          <CarNewsListing webView={webView} data={relatedNews} />
        </SectionItem>
      )}

      <SectionItem sectionKey="PopularBrands" pb={5}>
        <Stack gap={5} pt={4}>
          {isWeb && (
            <TitleDivider
              title="Popular Brands in Malaysia"
              paddingBlock="0px"
            />
          )}

          {isWeb ? (
            <BrandGroupList
              brands={settings?.car_brand_featured.slice(0, 12)}
              title={'Popular Brands in Malaysia'}
              webView={!!webView}
              isNewCar
              ItemGridProps={{
                lg: 1,
              }}
              sx={{
                backgroundColor: 'none',
                p: 0,
              }}
              showTitle={false}
              imageStyles={{
                maxHeight: 50,
              }}
            />
          ) : (
            <BrandGroupList
              brands={settings?.car_brand_featured.slice(
                0,
                isTabletView ? 18 : 16
              )}
              title={'Popular Brands in Malaysia'}
              webView={!!webView}
              isNewCar
            />
          )}
        </Stack>
      </SectionItem>
      <SectionItem sectionKey="SameBrand" hide={isEmpty(detail?.same_brand)}>
        <RelatedCarSection cars={detail?.same_brand} webView={webView} />
      </SectionItem>

      <SectionItem sectionKey="SameRange" hide={isEmpty(detail?.same_range)}>
        <SameRangeCarSection cars={detail?.same_range} />
      </SectionItem>

      {!webView && (
        <SectionItem sectionKey={'RecommendCars'}>
          <RecommendCarList webView={webView} />
        </SectionItem>
      )}
    </Stack>
  );
};

/**
 * ===========================
 * EXPORTS
 * ===========================
 */
export const CarDetailSectionContent = forwardRef(CarDetailSectionContentView);
